import { useCallback, useEffect, useState } from "react";
import { Eye, EyeOff, KeyRound, Loader2, Trash2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { fmtDate, generatePassword, passwordStrength } from "@/lib/format";
import { InviteOrPassword, inviteToast } from "@/components/InviteOrPassword";
import { InviteBadge } from "@/components/InviteBadge";

const TONES = { emerald: "bg-emerald-100 text-emerald-800", amber: "bg-amber-100 text-amber-800", red: "bg-red-100 text-red-700", slate: "bg-slate-100 text-slate-600" };

const PasswordReveal = ({ uid }) => {
  const [pw, setPw] = useState(null);
  const [busy, setBusy] = useState(false);
  const toggle = async () => {
    if (pw !== null) return setPw(null);
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/users/${uid}/password`);
      setPw(data.password ?? "");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const st = passwordStrength(pw);
  return (
    <div className="flex items-center gap-2 min-w-0">
      <span className="font-num text-sm truncate" data-testid={`user-${uid}-password`}>{pw === null ? "••••••••" : pw || "(ukjent)"}</span>
      {pw && <span className={`text-[11px] px-1.5 py-0.5 rounded font-medium ${TONES[st.tone]}`} data-testid={`user-${uid}-strength`}>{st.label}</span>}
      <button type="button" onClick={toggle} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" aria-label="Vis passord" data-testid={`user-${uid}-reveal`}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : pw === null ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
      </button>
    </div>
  );
};

const UserRow = ({ u, reload }) => {
  const [edit, setEdit] = useState(false);
  const [pw, setPw] = useState("");
  const savePw = async () => {
    try {
      await api.patch(`/admin/users/${u.id}`, { password: pw });
      toast.success("Passordet er endret");
      setEdit(false);
      setPw("");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const del = async () => {
    if (!window.confirm(`Slette brukeren ${u.email}?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="border border-slate-200 rounded-md p-3 space-y-2" data-testid={`user-row-${u.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-medium text-slate-900 truncate">{u.name || u.email}</div>
          <div className="text-xs text-slate-500 truncate">{u.email} · Sist innlogget {fmtDate(u.last_login)} · {u.login_count || 0} innlogginger</div>
        </div>
        <div className="flex shrink-0">
          <Button size="sm" variant="ghost" onClick={() => setEdit(!edit)} title="Endre passord" data-testid={`user-${u.id}-change-password`}><KeyRound className="w-4 h-4" /></Button>
          <Button size="sm" variant="ghost" onClick={del} className="text-slate-500 hover:text-red-600" title="Slett bruker" data-testid={`user-${u.id}-delete`}><Trash2 className="w-4 h-4" /></Button>
        </div>
      </div>
      {u.invite_pending ? <InviteBadge url={`/admin/users/${u.id}/invite`} testId={`user-${u.id}-invite`} /> : <PasswordReveal uid={u.id} key={u.password_changed_at || "x"} />}
      {edit && (
        <div className="flex gap-2">
          <Input value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Nytt passord (min. 8 tegn)" className="font-num" data-testid={`user-${u.id}-new-password`} />
          <Button variant="outline" onClick={() => setPw(generatePassword())} title="Generer">Generer</Button>
          <Button onClick={savePw} disabled={pw.length < 8} data-testid={`user-${u.id}-save-password`}>Lagre</Button>
        </div>
      )}
    </div>
  );
};

export const UsersDialog = ({ customer, onOpenChange, onChanged }) => {
  const [users, setUsers] = useState([]);
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [invite, setInvite] = useState(true);
  const cid = customer?.id;

  const load = useCallback(async () => {
    if (!cid) return;
    const { data } = await api.get(`/admin/customers/${cid}/users`);
    setUsers(data);
  }, [cid]);

  useEffect(() => {
    load();
  }, [load]);

  const add = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post(`/admin/customers/${cid}/users`, { ...f, password: invite ? null : f.password });
      inviteToast(toast, f.email, data.invite_sent);
      setF({ name: "", email: "", password: "" });
      load();
      onChanged();
    } catch (ex) {
      toast.error(errMsg(ex));
    }
  };

  return (
    <Dialog open={!!customer} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="users-dialog">
        <DialogHeader>
          <DialogTitle>Brukere – {customer?.name}</DialogTitle>
          <DialogDescription>Administrer innlogginger. Visning av passord logges i endringsloggen.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {users.map((u) => <UserRow key={u.id} u={u} reload={() => { load(); onChanged(); }} />)}
          {!users.length && <p className="text-sm text-slate-500">Ingen brukere.</p>}
        </div>
        <form onSubmit={add} className="mt-4 border-t border-slate-100 pt-4 space-y-2">
          <div className="grid sm:grid-cols-2 gap-2">
            <Input placeholder="Navn" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="add-user-name" />
            <Input placeholder="E-post" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="add-user-email" />
          </div>
          <InviteOrPassword invite={invite} setInvite={setInvite} password={f.password} setPassword={(v) => setF({ ...f, password: v })} testPrefix="add-user" />
          <Button type="submit" data-testid="add-user-submit"><UserPlus className="w-4 h-4 mr-1" />Legg til</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
